import React, { useState } from 'react';
import { adminApi } from '../api';
import { useAdmin } from './AdminContext';
import Modal, { Field } from './Modal';
import Markdown from '../markdown/Markdown';
import { Plus, Trash } from '../components/Icons';

const STATUSES = [
  { id: 'stable', label: 'Stable' },
  { id: 'beta', label: 'Beta' },
  { id: 'deprecated', label: 'Deprecated' },
  { id: 'hidden', label: 'Hidden (not on site)' },
];

const GUIDE = [
  ['Callout', ':::callout{type=warn}\nThe secret key is shown **once**.\n:::'],
  ['Endpoint', '::endpoint{method=POST path="/api/v1/sessions" auth="X-Api-Key"}'],
  ['Code block with title', '```json title="Response"\n{ "verified": true }\n```'],
  ['Steps', ':::steps\n### Create a client\nIn the portal, create OAuth credentials.\n\n### Connect\nPaste them into the dashboard.\n:::'],
  ['Badges', '| Field | Required |\n| --- | --- |\n| `mode` | :badge[Required] |\n| `POST` | :method[POST] |'],
  ['Internal link', 'See [Validate the token](validate-token).'],
];

function NewProductModal({ onClose, onDone }) {
  const [label, setLabel] = useState('');
  const [id, setId] = useState('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(null);
  const slug = touched ? id : label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const submit = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createProduct({ id: slug, label });
      await adminApi.createVersion(slug, { key: 'v1', label: 'v1', status: 'beta' });
      onDone();
    } catch (err) { setError(err.message); }
  };

  return (
    <Modal title="New product" onClose={onClose} footer={<>
      <button type="button" className="vt-btn vt-btn--soft vt-btn--sm" onClick={onClose}>Cancel</button>
      <button type="submit" form="new-product" className="vt-btn vt-btn--sm" disabled={!label || !slug}>Create product</button>
    </>}>
      <form id="new-product" className="cms-form" onSubmit={submit}>
        {error && <div className="cms-alert">{error}</div>}
        <Field label="Name"><input className="cms-input" autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Age Assurance" /></Field>
        <Field label="URL id" hint={`Docs will live at /${slug || '…'}/v1`}>
          <input className="cms-input" value={slug} onChange={(e) => { setTouched(true); setId(e.target.value.toLowerCase()); }} />
        </Field>
        <p className="cms-muted">A <strong>v1</strong> version is created in beta. Add pages from the sidebar.</p>
      </form>
    </Modal>
  );
}

function NewVersionModal({ product, onClose, onDone }) {
  const [key, setKey] = useState(`v${product.versions.length + 1}`);
  const [status, setStatus] = useState('beta');
  const [clone, setClone] = useState(product.versions[product.versions.length - 1]?.versionId || '');
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createVersion(product.id, { key, label: key, status, cloneFromVersionId: clone || null });
      onDone();
    } catch (err) { setError(err.message); }
  };

  return (
    <Modal title={`New ${product.label} version`} onClose={onClose} footer={<>
      <button type="button" className="vt-btn vt-btn--soft vt-btn--sm" onClick={onClose}>Cancel</button>
      <button type="submit" form="new-version" className="vt-btn vt-btn--sm" disabled={!key}>Create version</button>
    </>}>
      <form id="new-version" className="cms-form" onSubmit={submit}>
        {error && <div className="cms-alert">{error}</div>}
        <Field label="Version"><input className="cms-input" autoFocus value={key} onChange={(e) => setKey(e.target.value.toLowerCase())} /></Field>
        <Field label="Start from">
          <select className="cms-input" value={clone} onChange={(e) => setClone(e.target.value)}>
            <option value="">Empty version</option>
            {product.versions.map((v) => <option key={v.versionId} value={v.versionId}>Copy of {v.label} ({v.pages.length} pages)</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select className="cms-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </Field>
      </form>
    </Modal>
  );
}

export default function AdminHome() {
  const { tree, refreshTree } = useAdmin();
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [error, setError] = useState(null);

  const act = async (fn) => {
    setError(null);
    try { await fn(); await refreshTree(); } catch (e) { setError(e.message); }
  };

  return (
    <div className="cms-pad cms-home">
      <div className="cms-home-head">
        <div>
          <div className="vt-eyebrow">Docs CMS</div>
          <h1 className="docs-title" style={{ fontSize: 40 }}>Products &amp; versions</h1>
          <p className="docs-lead" style={{ margin: 0 }}>Everything published here appears on the public docs site within a minute. Drafts are only visible to admins.</p>
        </div>
        <button type="button" className="vt-btn vt-btn--sm" onClick={() => setModal({ type: 'product' })}><Plus size={14} /> New product</button>
      </div>

      {error && <div className="cms-alert">{error}</div>}

      <div className="cms-products">
        {tree?.map((p) => (
          <section key={p.id} className="cms-card">
            <div className="cms-card-head">
              <div>
                <h2>{p.label}</h2>
                <code className="docs-inline-code">/{p.id}</code>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="vt-btn vt-btn--soft vt-btn--sm" onClick={() => setModal({ type: 'version', product: p })}><Plus size={14} /> Version</button>
                <button type="button" className="docs-icon-btn" aria-label={`Delete ${p.label}`} onClick={() => setConfirm({ label: p.label, run: () => adminApi.deleteProduct(p.id) })}><Trash size={16} /></button>
              </div>
            </div>
            <table className="cms-table">
              <thead><tr><th>Version</th><th>Status</th><th>Pages</th><th>Live</th><th>Unpublished</th><th /></tr></thead>
              <tbody>
                {p.versions.map((v) => {
                  const live = v.pages.filter((x) => x.published).length;
                  const changed = v.pages.filter((x) => x.hasUnpublishedChanges).length;
                  return (
                    <tr key={v.versionId}>
                      <td><strong>{v.label}</strong></td>
                      <td>
                        <select className="cms-input cms-input--sm" value={v.status} onChange={(e) => act(() => adminApi.updateVersion(v.versionId, { status: e.target.value }))}>
                          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                      </td>
                      <td>{v.pages.length}</td>
                      <td>{live}</td>
                      <td>{changed > 0 ? <span className="cms-status cms-status--changed"><i className="cms-dot cms-dot--changed" />{changed}</span> : '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button type="button" className="docs-icon-btn" aria-label={`Delete ${v.label}`} onClick={() => setConfirm({ label: `${p.label} ${v.label}`, run: () => adminApi.deleteVersion(v.versionId) })}><Trash size={16} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        ))}
      </div>

      <section className="cms-guide">
        <div className="vt-eyebrow">Writing guide</div>
        <h2>Markdown, plus a few Veritage blocks</h2>
        <p className="cms-muted">Pages are GitHub-flavoured markdown. Use the editor toolbar to insert these, or type them directly.</p>
        <div className="cms-guide-grid">
          {GUIDE.map(([title, md]) => (
            <div key={title} className="cms-guide-item">
              <div className="vt-eyebrow">{title}</div>
              <pre className="cms-guide-src">{md}</pre>
              <div className="cms-guide-out"><Markdown content={md} basePath="/wallet-hub/v1" /></div>
            </div>
          ))}
        </div>
      </section>

      {modal?.type === 'product' && <NewProductModal onClose={() => setModal(null)} onDone={async () => { setModal(null); await refreshTree(); }} />}
      {modal?.type === 'version' && <NewVersionModal product={modal.product} onClose={() => setModal(null)} onDone={async () => { setModal(null); await refreshTree(); }} />}
      {confirm && (
        <Modal title={`Delete ${confirm.label}?`} onClose={() => setConfirm(null)} footer={<>
          <button type="button" className="vt-btn vt-btn--soft vt-btn--sm" onClick={() => setConfirm(null)}>Cancel</button>
          <button type="button" className="vt-btn vt-btn--sm cms-btn-danger" onClick={() => { const c = confirm; setConfirm(null); act(c.run); }}>Delete permanently</button>
        </>}>
          <p style={{ margin: 0 }}>All pages and publish history under <strong>{confirm.label}</strong> will be removed. This can't be undone.</p>
        </Modal>
      )}
    </div>
  );
}
